import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { hasRole, ROLES } from '../utils/rbac';
import { HiOutlineMenuAlt3, HiX } from 'react-icons/hi';
import { FiUser, FiLogOut, FiLayout, FiShield, FiChevronDown } from 'react-icons/fi';

function NavigationModern({ isAuthenticated, user, onLogout }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/lawyers', label: 'Find a Lawyer' },
    { path: '/case-studies', label: 'Case Studies' },
    { path: '/about', label: 'About' },
    { path: '/team', label: 'Our Team' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-white/95 backdrop-blur-xl shadow-nav py-2' 
        : 'bg-white/70 backdrop-blur-md py-4'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow duration-300">
              <span className="text-white text-lg">⚖️</span>
            </div>
            <span className="text-xl font-bold text-secondary-900 tracking-tight">
              Bihar<span className="text-gradient"> Lawyer</span>
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-secondary-600 hover:text-primary-500 hover:bg-secondary-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Auth Section */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive('/dashboard')
                      ? 'text-primary-500 bg-primary-50'
                      : 'text-secondary-600 hover:text-primary-500 hover:bg-secondary-50'
                  }`}
                >
                  <FiLayout className="w-4 h-4" />
                  Dashboard
                </Link>

                {(hasRole(user?.user_type, ROLES.SUPER_ADMIN) || hasRole(user?.user_type, ROLES.ADMIN)) && (
                  <Link
                    to="/admin"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive('/admin')
                        ? 'text-primary-500 bg-primary-50'
                        : 'text-secondary-600 hover:text-primary-500 hover:bg-secondary-50'
                    }`}
                  >
                    <FiShield className="w-4 h-4" />
                    Admin
                  </Link>
                )}

                {/* User Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2.5 pl-3 pr-2 py-1.5 rounded-xl hover:bg-secondary-50 transition-all duration-200 border border-secondary-200"
                  >
                    {user?.profile_image ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL}${user.profile_image}`}
                        alt={user?.full_name}
                        className="w-8 h-8 rounded-lg object-cover ring-2 ring-accent-400"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-teal-500 flex items-center justify-center">
                        <FiUser className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="text-left hidden xl:block">
                      <p className="text-sm font-semibold text-secondary-900 leading-tight">{user?.full_name || 'User'}</p>
                      <p className="text-xs text-secondary-400 capitalize">{user?.user_type?.replace('_', ' ')}</p>
                    </div>
                    <FiChevronDown className={`w-4 h-4 text-secondary-400 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {userMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-secondary-100 py-2 z-50 animate-fade-in">
                        <div className="px-4 py-3 border-b border-secondary-100">
                          <p className="text-sm font-semibold text-secondary-900">{user?.full_name}</p>
                          <p className="text-xs text-secondary-400">{user?.email}</p>
                        </div>
                        <Link
                          to="/dashboard"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-secondary-600 hover:bg-secondary-50 hover:text-primary-500 transition-colors"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <FiLayout className="w-4 h-4" />
                          Dashboard
                        </Link>
                        <div className="border-t border-secondary-100 mt-1 pt-1">
                          <button
                            onClick={() => {
                              onLogout();
                              setUserMenuOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-danger-500 hover:bg-danger-50 transition-colors"
                          >
                            <FiLogOut className="w-4 h-4" />
                            Sign Out
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium text-secondary-600 hover:text-primary-500 rounded-lg hover:bg-secondary-50 transition-all duration-200"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="btn-primary btn-sm text-sm"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-lg text-secondary-600 hover:bg-secondary-100 transition-colors"
          >
            {isMenuOpen ? <HiX className="w-6 h-6" /> : <HiOutlineMenuAlt3 className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden transition-all duration-300 ease-in-out overflow-hidden ${
        isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-4 pt-4 pb-6 bg-white border-t border-secondary-100 shadow-lg">
          <div className="space-y-1">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-secondary-600 hover:text-primary-500 hover:bg-secondary-50'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {isAuthenticated ? (
              <>
                <div className="border-t border-secondary-100 my-3" />
                <Link
                  to="/dashboard"
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive('/dashboard')
                      ? 'text-primary-500 bg-primary-50'
                      : 'text-secondary-600 hover:bg-secondary-50'
                  }`}
                >
                  <FiLayout className="w-4 h-4" />
                  Dashboard
                </Link>

                {(hasRole(user?.user_type, ROLES.SUPER_ADMIN) || hasRole(user?.user_type, ROLES.ADMIN)) && (
                  <Link
                    to="/admin"
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      isActive('/admin')
                        ? 'text-primary-500 bg-primary-50'
                        : 'text-secondary-600 hover:bg-secondary-50'
                    }`}
                  >
                    <FiShield className="w-4 h-4" />
                    Admin Panel
                  </Link>
                )}

                <div className="border-t border-secondary-100 my-3" />
                <div className="flex items-center gap-3 px-4 py-3">
                  {user?.profile_image ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${user.profile_image}`}
                      alt={user?.full_name}
                      className="w-10 h-10 rounded-xl object-cover ring-2 ring-accent-400"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-teal-500 flex items-center justify-center">
                      <FiUser className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-secondary-900">{user?.full_name || 'User'}</p>
                    <p className="text-xs text-secondary-400 capitalize">{user?.user_type?.replace('_', ' ')}</p>
                  </div>
                </div>
                <button
                  onClick={() => { onLogout(); setIsMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-danger-500 hover:bg-danger-50 transition-colors"
                >
                  <FiLogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <div className="border-t border-secondary-100 my-3" />
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-3 rounded-xl text-sm font-medium text-secondary-600 hover:bg-secondary-50 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-3 rounded-xl text-sm font-semibold text-center text-white transition-colors"
                  style={{ backgroundColor: '#1e3a5f' }}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default NavigationModern;
